import React, { useEffect, useRef, useState } from "react";
import he from "he";
import AlarmOnIcon from "@mui/icons-material/AlarmOn";
import ChairAltIcon from "@mui/icons-material/ChairAlt";
import { logClientActivity } from "../../utils/clientLogger";
import { isCompatibilityMouseEvent } from "./touchInput";

let interactionSequence = 0;

// TicketItem component displays a single item in a ticket, including timers, modifiers, and interaction handling.
export default function TicketItem({
  item,
  ticketInstanceId,
  onTap,
  onDoubleClick,
  ticketTouchStateRef = null,
  showCategory = true,
  settings = {},
  isBumpBarSelected = false,
  isPrepDisplayDarkened = false,
  cookStartDelaySeconds = null,
  cookTimingStartMs = null,
}) {
  const showItemBumpDisplayBoxes =
    settings?.display_bump_status_of_other_units === true &&
    settings?.display_bump_status_other_units_item === true &&
    Array.isArray(item?.item_bump_display_boxes) &&
    item.item_bump_display_boxes.length > 0;

  const UI_SCALE_CONFIG = {
    small: {
      categoryLabel: "text-[10px]",
      firedLabel: "text-[10px]",
      quantity: "text-xs",
      itemName: "text-xs",
      modifiers: "text-[11px]",
      seatInfo: "text-sm",
      timer: "text-[10px]",
      bumpedMessage: "text-[10px]",
      voidMessage: "text-xs",
      addonLabel: "text-[10px]",
    },

    medium: {
      categoryLabel: "text-md",
      firedLabel: "text-sm",
      quantity: "text-md",
      itemName: "text-md",
      modifiers: "text-md",
      seatInfo: "text-lg",
      timer: "text-md",
      bumpedMessage: "text-sm",
      voidMessage: "text-md",
      addonLabel: "text-sm",
    },

    large: {
      categoryLabel: "text-xl",
      firedLabel: "text-lg",
      quantity: "text-xl",
      itemName: "text-xl",
      modifiers: "text-lg",
      seatInfo: "text-2xl",
      timer: "text-xl",
      bumpedMessage: "text-lg",
      voidMessage: "text-xl",
      addonLabel: "text-lg",
    },

    extralarge: {
      categoryLabel: "text-2xl",
      firedLabel: "text-xl",
      quantity: "text-2xl",
      itemName: "text-2xl",
      modifiers: "text-xl",
      seatInfo: "text-4xl",
      timer: "text-2xl",
      bumpedMessage: "text-xl",
      voidMessage: "text-2xl",
      addonLabel: "text-xl",
    },
    test: {
      categoryLabel: "text-7xl",
      firedLabel: "text-7xl",
      quantity: "text-7xl",
      itemName: "text-7xl",
      modifiers: "text-7xl",
      seatInfo: "text-7xl",
      timer: "text-7xl",
      bumpedMessage: "text-7xl",
      voidMessage: "text-7xl",
      addonLabel: "text-7xl",
    },
  };

  const uiScaleKey = settings.uiscale || "medium";
  const uiScale = UI_SCALE_CONFIG[uiScaleKey] || UI_SCALE_CONFIG.medium;

  // Utility function that turns things like "&amp;" into "&" so the text looks normal
  const safeDecode = (value) =>
    typeof value === "string" ? he.decode(value) : "";

  // Check if voided or a course separator; also get seat info
  const isVoided = !!item.isVoided;
  const isCourseSeparator = safeDecode(item?.Category) === "Course Separator";
  const seat = item?.PreModifier?.S0 || "";
  const lastTouchRef = useRef(0);
  const activeTouchRef = useRef(null);
  const localTouchStateRef = useRef({ suppressUntil: 0 });
  const touchStateRef = ticketTouchStateRef || localTouchStateRef;
  useEffect(() => {
    lastTouchRef.current = 0;
    activeTouchRef.current = null;
  }, [item.ID, ticketInstanceId]);
  const itemRef = useRef(null);
  const interactionInstanceRef = useRef(null);
  if (interactionInstanceRef.current === null) {
    interactionInstanceRef.current = ++interactionSequence;
  }

  const logInteraction = (event, decision, extra = {}) => {
    const native = event?.nativeEvent || event;
    const touch = event?.changedTouches?.[0] || event?.touches?.[0];
    logClientActivity("kds.item_input", {
      ticket_instance_id: ticketInstanceId,
      item_id: item.ID,
      item_name: item.Name,
      course: item.course ?? null,
      is_bumped: !!item.isBumped,
      component_instance: interactionInstanceRef.current,
      event_type: event?.type ?? null,
      event_timestamp_ms: event?.timeStamp ?? null,
      client_time_ms: Date.now(),
      is_trusted: native?.isTrusted ?? null,
      click_count: event?.detail ?? null,
      fires_touch_events: native?.sourceCapabilities?.firesTouchEvents ?? null,
      touch_count: event?.touches?.length ?? null,
      touch_id: touch?.identifier ?? null,
      client_x: touch?.clientX ?? event?.clientX ?? null,
      client_y: touch?.clientY ?? event?.clientY ?? null,
      decision,
      ...extra,
    });
  };

  useEffect(() => {
    if (!isBumpBarSelected) return;
    itemRef.current?.scrollIntoView({
      block: "center",
      inline: "nearest",
      behavior: "smooth",
    });
  }, [isBumpBarSelected]);

  const emitTap = () => {
    if (isCourseSeparator || isVoided || typeof onTap !== "function") return;
    onTap(ticketInstanceId, item.ID);
  };

  const handleTouchStart = (event) => {
    const touch = event.touches?.[0];
    activeTouchRef.current = event.touches?.length === 1 ? {
      id: touch.identifier, x: touch.clientX, y: touch.clientY,
      itemId: item.ID, ticketId: ticketInstanceId, startedAt: Date.now(),
    } : null;
    if (!activeTouchRef.current) lastTouchRef.current = 0;
    touchStateRef.current.suppressUntil = Date.now() + 700;
    logInteraction(event, "gesture_started");
  };

  const handleTouchMove = (event) => {
    const gesture = activeTouchRef.current;
    const touch = Array.from(event.touches || []).find(t => t.identifier === gesture?.id);
    if (gesture && (!touch || Math.hypot(touch.clientX - gesture.x, touch.clientY - gesture.y) > 20)) {
      activeTouchRef.current = null;
      lastTouchRef.current = 0;
      logInteraction(event, "gesture_cancelled_movement");
    }
  };

  const handleTouchEnd = (event) => {
    const gesture = activeTouchRef.current;
    activeTouchRef.current = null;
    const now = Date.now();
    touchStateRef.current.suppressUntil = now + 700;
    const touch = Array.from(event.changedTouches || []).find(t => t.identifier === gesture?.id);
    if (!gesture || !touch || event.touches.length || gesture.itemId !== item.ID ||
        gesture.ticketId !== ticketInstanceId || now - gesture.startedAt > 300 ||
        Math.hypot(touch.clientX - gesture.x, touch.clientY - gesture.y) > 20) {
      lastTouchRef.current = 0;
      logInteraction(event, "gesture_cancelled");
      return;
    }
    const sinceLastTouchMs = lastTouchRef.current ? now - lastTouchRef.current : null;
    const isDoubleTap = sinceLastTouchMs !== null && sinceLastTouchMs < 300;
    lastTouchRef.current = isDoubleTap ? 0 : now;
    if (isDoubleTap && !isCourseSeparator && !isVoided && typeof onDoubleClick === "function") {
      // Prevent compatibility clicks before the callback can hide this row.
      event.preventDefault();
      event.stopPropagation();
      logInteraction(event, "invoke_double_tap", { since_last_touch_ms: sinceLastTouchMs });
      onDoubleClick(gesture.ticketId, gesture.itemId);
    } else {
      logInteraction(event, "no_double_tap", { since_last_touch_ms: sinceLastTouchMs });
    }
  };

  const handleTouchCancel = (event) => {
    activeTouchRef.current = null;
    lastTouchRef.current = 0;
    touchStateRef.current.suppressUntil = Date.now() + 700;
    logInteraction(event, "gesture_cancelled");
  };

  const handleClick = (event) => {
    if (isCompatibilityMouseEvent(event, touchStateRef.current)) {
      logInteraction(event, "suppressed_after_touch");
      return;
    }
    logInteraction(event, isCourseSeparator || isVoided || typeof onTap !== "function" ? "ignored" : "invoke_tap");
    emitTap();
  };

  const handleMouseDoubleClick = (event) => {
    if (isCompatibilityMouseEvent(event, touchStateRef.current)) {
      logInteraction(event, "suppressed_after_touch_double_tap");
      return;
    }
    if (isCourseSeparator || isVoided || typeof onDoubleClick !== "function") {
      logInteraction(event, "ignored");
      return;
    }
    logInteraction(event, "invoke_double_click");
    onDoubleClick(ticketInstanceId, item.ID);
  };
  const explicitCookStartDelaySeconds = Number(cookStartDelaySeconds ?? item?.cookStartDelaySeconds ?? 0);
  const prepSeconds = Number.isFinite(explicitCookStartDelaySeconds) && explicitCookStartDelaySeconds > 0
    ? Math.round(explicitCookStartDelaySeconds)
    : 0;

  // Track signed elapsed time. Cook-time balancing can delay shorter items below zero.
  const [timer, setTimer] = useState(0);
  const [phase, setPhase] = useState(prepSeconds ? "prep" : "countup");

  // Park-aware timer logic
  useEffect(() => {
    if (isCourseSeparator || item.isBumped || isVoided) return;

    const providedStartMs = cookTimingStartMs == null ? NaN : Number(cookTimingStartMs);
    const created = new Date(item.createdAt);
    const startBaseMs = Number.isFinite(providedStartMs) ? providedStartMs : created.getTime();
    if (!Number.isFinite(startBaseMs)) return;

    const computeAndSet = () => {
      const startMs = startBaseMs;

      // While parked, we freeze at parkedAt; otherwise use now
      const endMs =
        item.isParked && item.parkedAt
          ? new Date(item.parkedAt).getTime()
          : Date.now();

      const pausedMs = (item.totalPausedSeconds || 0) * 1000;

      const activeSeconds = Math.max(
        0,
        Math.floor((endMs - startMs - pausedMs) / 1000)
      );

      const signedSeconds = activeSeconds - prepSeconds;
      setPhase(signedSeconds < 0 ? "prep" : "countup");
      setTimer(signedSeconds);
    };

    computeAndSet();

    if (item.isParked && item.parkedAt) {
      return undefined;
    }

    const interval = setInterval(computeAndSet, 1000);
    return () => clearInterval(interval);
  }, [
    item.createdAt,
    cookTimingStartMs,
    prepSeconds,
    item.isBumped,
    item.isVoided,
    item.isParked,
    item.parkedAt,
    item.totalPausedSeconds,
    isCourseSeparator,
    isVoided,
  ]);

  const formatTime = (seconds) => {
    const sign = seconds < 0 ? "-" : "";
    const absSeconds = Math.abs(seconds);
    const min = Math.floor(absSeconds / 60);
    const sec = (absSeconds % 60).toString().padStart(2, "0");
    return `${sign}${min}:${sec}`;
  };

  const getTimerDisplay = () => {
    if (item.isBumped || isVoided || isCourseSeparator) return null;

    const icon = (
      <AlarmOnIcon
        fontSize={`${uiScale.seatInfo}`}
        className="mr-1 align-middle text-green-600"
      />
    );

    const timeText = `${formatTime(timer)}`;

    return (
      <span className={`inline-flex items-center ${uiScale.timer}`}>
        {icon}
        {timeText}
      </span>
    );
  };

  const getTextColor = () => {
    if (item.isBumped || isVoided || isCourseSeparator) {
      return "text-gray-400 italic dark:text-gray-500";
    }

    return "text-green-700 dark:text-green-400";
  };

  if (isCourseSeparator) {
    return (
      <li className="my-2 px-4 py-1 border border-dashed border-gray-300 rounded text-center text-gray-500 text-[12px] uppercase tracking-wide bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-400">
        Course Separator
      </li>
    );
  }

  const decodedName = safeDecode(item?.Name);
  const decodedModifiers = Object.entries(item?.PreModifier || {})
    .filter(([key]) => key.startsWith("S") && key !== "S0")
    .map(([_, val]) => safeDecode(val));
  const decodedCategory = safeDecode(item?.Category);

  const combineCondimentModifiers = (condiments) => {
    if (settings.combine_condiments === false) {
      return condiments.map((c) => ({
        ...c,
        Name: safeDecode(c?.Name || "").trim(),
      }));
    }

    const prefixes = new Set(["NO", "EXTRA", "ONLY", "LIGHT", "ADD"]);
    const suffixes = new Set(["ON THE SIDE"]);

    const toTitleCase = (str) =>
      str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());

    const normalize = (str) =>
      str.replace(/^-/, "").trim().toUpperCase().replace(/\s+/g, " ");

    const result = [];

    for (let i = 0; i < condiments.length; i += 1) {
      const current = condiments[i];
      const next = condiments[i + 1];

      const rawCurr = safeDecode(current?.Name || "");
      const rawNext = safeDecode(next?.Name || "");
      const currNorm = normalize(rawCurr);
      const nextNorm = normalize(rawNext);

      if (prefixes.has(currNorm) && next) {
        const cleanNext = rawNext.replace(/^-/, "").trim();
        const combinedText = `- ${toTitleCase(currNorm)} ${cleanNext}`;

        result.push({
          ...next,
          Name: combinedText,
        });

        i += 1;
      } else if (next && suffixes.has(nextNorm)) {
        const cleanCurrent = rawCurr.replace(/^-/, "").trim();
        result.push({
          ...current,
          Name: `- ${toTitleCase(cleanCurrent)} On The Side`,
        });
        i += 1;
      } else if (suffixes.has(currNorm)) {
        result.push({
          ...current,
          Name: rawCurr.trim(),
        });
      } else if (!prefixes.has(currNorm) && !suffixes.has(currNorm)) {
        result.push({
          ...current,
          Name: rawCurr.trim(),
        });
      }
    }

    return result;
  };

  return (
    <li
      ref={itemRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchCancel}
      onClick={handleClick}
      onDoubleClick={handleMouseDoubleClick}
      className={`p-2 rounded border text-sm transition-all duration-200 cursor-pointer touch-manipulation select-none
    ${isBumpBarSelected ? "ring-4 ring-inset ring-yellow-400 shadow-[inset_0_0_0_1px_rgba(161,98,7,0.55),0_0_16px_rgba(250,204,21,0.35)] relative z-20" : ""}
    ${item.showsAddon ? "animate-pulse-fade border-orange-400" : ""}
    ${isPrepDisplayDarkened && !item.isBumped && !isVoided ? "opacity-45 grayscale bg-gray-200 text-gray-600 dark:bg-gray-950 dark:text-gray-400" : ""}
    ${item.isFired && !item.isBumped && !isVoided
          ? "border-red-600 border-2 bg-red-50 dark:bg-red-950"
          : ""
        }
    ${item.isBumped
          ? "bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400 line-through opacity-40"
          : isVoided
            ? "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400 line-through"
            : isPrepDisplayDarkened
              ? "bg-gray-200 text-gray-600 dark:bg-gray-950 dark:text-gray-400"
            : phase === "overtime"
              ? "bg-blue-50 dark:bg-blue-900"
              : "bg-white text-black dark:bg-gray-900 dark:text-gray-100"
        }`}
    >
      {showItemBumpDisplayBoxes && (
        <div className="mb-2">
          <div className="flex items-center gap-2">
            {item.item_bump_display_boxes.map((box) => {
              const isReceived = !!box?.received;
              const isBumped = !!box?.bumped;
              const isSelf = !!box?.is_self;
              return (
                <div
                  key={`item-box-${item.ID}-${box.station_id}`}
                  className={`relative h-4 w-4 rounded-sm border transition-all duration-200 ${
                    isReceived ? "opacity-100" : "opacity-0"
                  } ${
                    isBumped
                      ? "bg-gray-200 border-gray-400 dark:bg-slate-800 dark:border-slate-500"
                      : isSelf
                        ? "bg-transparent border-yellow-500 dark:border-yellow-400"
                        : "bg-white/70 border-black/20 dark:border-white/20 dark:bg-gray-900/60"
                  }`}
                  aria-hidden={!isReceived}
                >
                  {isReceived && isBumped && (
                    <>
                      <div className="absolute left-[-2px] right-[-2px] top-1/2 -translate-y-1/2 rotate-45 border-t-2 border-black/60 dark:border-white/60" />
                      <div className="absolute left-[-2px] right-[-2px] top-1/2 -translate-y-1/2 -rotate-45 border-t-2 border-black/60 dark:border-white/60" />
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {item.showsAddon && (
        <div className={`${uiScale.addonLabel} text-orange-600 font-semibold uppercase tracking-wide mb-1 flex items-center gap-2`}>
          Add-on Item
        </div>
      )}

      {item.isFired && !item.isBumped && !isVoided && (
        <div className={`${uiScale.firedLabel} text-red-600 font-semibold uppercase tracking-wide mb-1`}>
          Fired Item
        </div>
      )}

      {isVoided && (
        <div className="relative mt-2 h-4">
          <div
            className={`absolute inset-0 ${uiScale.voidMessage} text-red-500 italic text-center w-full`}
            style={{
              textDecoration: "none",
              zIndex: 10,
              pointerEvents: "none",
            }}
          >
            Voided
            {decodedModifiers.length > 0
              ? ` - ${decodedModifiers.join(", ")}`
              : ""}
          </div>
        </div>
      )}

      <div className="flex flex-col gap-1">
        {showCategory && decodedCategory && (
          <div className={`${uiScale.categoryLabel} text-purple-500 dark:text-purple-300 uppercase font-semibold tracking-wide`}>
            {decodedCategory}
          </div>
        )}
      </div>

      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 w-full">
        <div className="flex items-center gap-2">
          {item.Quantity !== 1 && (
            <span className={`font-bold ${uiScale.quantity} text-purple-600 dark:text-purple-300 shrink-0 pl-2`}>
              x{item.Quantity}
            </span>
          )}
          <span className={`font-medium ${uiScale.itemName} pl-2`}>{decodedName}</span>
        </div>

        <div className="justify-self-center">
          {settings.show_seat_number !== false && seat && (
            <span className={`text-gray-500 dark:text-gray-300 ${uiScale.seatInfo}`} title={seat}>
              <ChairAltIcon fontSize={`${uiScale.seatInfo}`} className="mr-1 align-middle" />
              <span>{seat.replace(/seat\s*/i, "")}</span>
            </span>
          )}
        </div>

        <div className="justify-self-end text-xs">
          {item.isBumped ? (
            <span className={`${uiScale.bumpedMessage} text-green-600 font-semibold`}>BUMPED</span>
          ) : (
            getTimerDisplay() && (
              <span className={getTextColor()}>{getTimerDisplay()}</span>
            )
          )}
        </div>
      </div>

      {decodedModifiers.length > 0 && (
        <ul className={`ml-3 mt-1 grid grid-cols-2 gap-x-4 gap-y-1 ${uiScale.modifiers} text-gray-600 dark:text-gray-400 font-normal text-left justify-center`}>
          {decodedModifiers.map((mod, idx) => (
            <li key={idx} className="flex items-start gap-1">
              - {mod}
            </li>
          ))}
        </ul>
      )}

      {item.Condiment && (
        <ul className={`ml-3 mt-1 grid grid-cols-2 gap-x-4 gap-y-1 ${uiScale.modifiers} text-gray-600 dark:text-gray-400 font-normal text-left justify-center`}>
          {combineCondimentModifiers(
            Array.isArray(item.Condiment) ? item.Condiment : [item.Condiment]
          ).map((c, idx) => (
            <li key={idx} className="flex items-start gap-1">
              - {safeDecode(c?.Name)}
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}
